import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  ProcessCellForExportParams,
} from '@ag-grid-community/core'
import { Component, Input, OnDestroy } from '@angular/core'
import {
  HeliosColumnTypes,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosColumnTypes,
  heliosDefaultColWidth,
} from '@helios/ag-grid-config'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NfColumnTypes,
  NfTooltipParams,
  dateTimeFormatter,
} from '@northfork/ag-grid'
import {
  composeFieldPath,
  formatDate,
  ISODateTimeFormat,
} from '@northfork/basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { JobsKey } from '../../../panel-key'
import { durationCellRenderer } from '../../grid-helpers/job-duration-cell-renderer'
import { jobStatusCellRenderer } from '../../grid-helpers/jobs-status-cell-renderer'
import { Status } from '../../model/job.model'
import { JobsService } from '../../services/jobs.service'
const statusPath = composeFieldPath([] as Status[])

const StatusGridKey = `${JobsKey}-StatusGrid`

@Component({
  selector: 'hs-status',
  templateUrl: './status.component.html',
})
export class StatusComponent implements OnDestroy {
  readonly gridOptions: GridOptions = this.createGridOptions()
  data: Status[] = []
  private readonly destroySubject$ = new Subject<void>()

  isLoading = false
  @Input()
  set jobId(value: number) {
    this.loadData(value)
  }

  constructor(
    private readonly jobsService: JobsService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async loadData(jobId: number) {
    this.isLoading = true
    const data = await this.jobsService.getStatus(jobId).toPromise()

    this.data = data
    this.isLoading = false
  }

  /** @internal */
  createGridOptions(): GridOptions {
    return {
      columnTypes: heliosColumnTypes,
      columnDefs: this.createBaseColumnDefs(),
      onGridReady: event => this.onGridReady(event),
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (params.column.getColDef().type === 'DateTime' && params.value) {
          return formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat)
        }
        return params.value
      },
    }
  }

  private onGridReady(event: GridReadyEvent) {
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.savedViewService.manageSavedView(
      event,
      StatusGridKey,
      this.destroySubject$,
    )

    enableHeliosCopyBehavior(event.api)
  }

  private createBaseColumnDefs(): ColDef[] {
    return [
      {
        field: statusPath('ts'),
        headerName: 'Timestamp',
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params =>
          formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
          params.value,
        width: heliosDefaultColWidth.DateTime,
        sortIndex: 0,
        sort: 'asc',
      },
      {
        field: statusPath('status'),
        type: NfColumnTypes.Identifier,
        cellRenderer: jobStatusCellRenderer,
        width: heliosDefaultColWidth.SmallIcon,
        tooltipValueGetter: params => params.value,
        tooltipComponentParams: {
          showTooltip: () => true,
        } as NfTooltipParams,
      },
      { field: statusPath('server'), type: NfColumnTypes.Identifier },
      { field: statusPath('duration'), cellRenderer: durationCellRenderer },
    ]
  }
}
